import Chance from "chance";
import { map, reduce, replace } from "lodash";
import moment from "moment-timezone";
import { Range } from "@linkfluence/models/date";
import { Locale, getAllLocales } from "@linkfluence/models/locale";
import { Platform } from "@linkfluence/models/platform";
import { diff, now } from "@tootools/date";
import { Glyph } from "@ui-kit/icon";
import { Icons, Pictures, SocialContent } from "./data";
import generateRandomWalk from "./random-walk";

export type Unit = "hours" | "days" | "months";
const chance = new Chance(1984);

const COMPANY_CLEAN = [
  " Inc.",
  ", Inc",
  ", Corp.",
  ", Ltd.",
  " Corp.",
  " Ltd.",
  ", Co.",
  " Co.",
  ", Corporation",
  " Corporation",
  ", LLC",
];

const TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/New_York",
  "Indian/Mauritius",
  "Europe/Berlin",
  "Asia/Shanghai",
  "Asia/Colombo",
];

export function getChance() {
  return chance;
}

export function screenName() {
  return chance.twitter();
}

export function socialName() {
  return chance.name();
}

export function email() {
  return chance.email();
}

export function random(min: number, max: number) {
  if (min < 0) {
    const random = chance.natural({ min: 0, max: max - min });
    return random + min;
  } else {
    return chance.natural({ min, max });
  }
}

export function platform() {
  const platforms = Object.values(Platform);
  return platforms[Math.floor(Math.random() * platforms.length)];
}

export function locale(): Locale {
  return randomValue(map(getAllLocales(), "id"));
}

export function evolution() {
  return random(-100, 100);
}

export function trueFalse() {
  return chance.coin() === "tails";
}

export function uid() {
  return chance.google_analytics() + "_" + chance.fbid();
}

export function hashtag(withHashtag: boolean = false) {
  return withHashtag ? chance.hashtag() : chance.hashtag().slice(1);
}

export function name() {
  return reduce(
    COMPANY_CLEAN,
    (acc, replacement) => {
      return replace(acc, replacement, "");
    },
    chance.company()
  );
}

export function percent() {
  return random(0, 100);
}

export function color() {
  function getColor(): string {
    const color = chance.color({ format: "hex" });
    if (color === "#ffffff" || color === "#000000") {
      return getColor() as string;
    } else {
      return color;
    }
  }
  return getColor();
}

export function colorFromSeed(seed: number | string) {
  const SeededChance = new Chance(seed);
  const color = SeededChance.color({ format: "hex" });
  return color;
}

export function url() {
  return chance.url();
}

export function latitude() {
  return chance.latitude();
}

export function longitude() {
  return chance.longitude();
}

export function zipCode() {
  return chance.zip();
}

export function sentence(words: number = 5) {
  return chance.sentence({ words });
}

export function word(length: number = 5) {
  return chance.word({ length });
}

export function paragraph(sentences: number = 1) {
  return chance.paragraph({ sentences });
}

export function randomValue<T>(array: readonly T[]): T {
  return array[random(0, array.length - 1)];
}

export function timezone() {
  return randomValue(TIMEZONES);
}

export function picture() {
  return randomValue(Pictures);
}

export function socialContent() {
  return randomValue(SocialContent);
}

export function icon() {
  return randomValue(Icons) as Glyph;
}

export function id(max: number = 5000) {
  return random(1, max);
}

export function date() {
  const date = moment(chance.date()).format("YYYY-MM-DDTHH:mm:ss");
  return new Date(date + "+00:00");
}

export const randomWalk = generateRandomWalk;

export function randomWalkFromRange(range: Range, rangeMax: number = 5000) {
  const steps = diff(range.from, range.to, range.interval, true);
  return randomWalk(steps, rangeMax);
}

export function largeNumber() {
  return chance.integer({ min: 3000, max: 20000 });
}

export function smallNumber() {
  return chance.integer({ min: 0, max: 500 });
}

export const getSerie = (steps: number, unit: Unit) => {
  const serie = randomWalk(steps);
  return map(serie, (val, index) => ({
    count: val,
    date: now("UTC").add(index, unit).format(),
  }));
};

export function gender() {
  return chance.gender();
}

export function country(fullCountryName?: boolean) {
  return chance.country({ full: fullCountryName });
}

export const emoji = () =>
  randomValue([
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "🤣",
    "😂",
    "🙂",
    "🙃",
    "😉",
    "😊",
    "😇",
    "🥰",
    "😍",
    "🤩",
    "😘",
    "😗",
    "☺",
    "😚",
    "😙",
    "😋",
    "😛",
    "😜",
    "🤪",
    "😝",
    "🤑",
    "🤗",
    "🤭",
    "🤫",
    "🤔",
    "🤐",
    "🤨",
    "😐",
    "😑",
    "😶",
    "😏",
    "😒",
    "🙄",
    "😬",
    "🤥",
    "😌",
    "😔",
    "😪",
    "🤤",
    "😴",
    "😷",
    "🤒",
    "🤕",
    "🤢",
    "🤮",
    "🤧",
    "🥵",
    "🥶",
    "🥴",
    "😵",
    "🤯",
    "🤠",
    "🥳",
    "😎",
    "🤓",
    "🧐",
    "😕",
    "😟",
    "🙁",
    "☹",
    "😮",
    "😯",
    "😲",
    "😳",
    "🥺",
    "😦",
    "😧",
    "😨",
    "😰",
    "😥",
    "😢",
    "😭",
    "😱",
    "😖",
    "😣",
    "😞",
    "😓",
    "😩",
    "😫",
    "😤",
    "😡",
    "😠",
    "🤬",
    "😈",
    "👿",
    "💀",
    "☠",
    "💩",
    "🤡",
    "👹",
    "👺",
    "👻",
    "👽",
    "👾",
    "🤖",
    "😺",
    "😸",
    "😹",
    "😻",
    "😼",
    "😽",
    "🙀",
    "😿",
    "😾",
    "💋",
    "👋",
    "🤚",
    "🖐",
    "✋",
    "🖖",
    "👌",
    "✌",
    "🤞",
    "🤟",
    "🤘",
    "🤙",
    "👈",
    "👉",
    "👆",
    "🖕",
    "👇",
    "☝",
    "👍",
    "👎",
    "✊",
    "👊",
    "🤛",
    "🤜",
    "👏",
    "🙌",
    "👐",
    "🤲",
    "🤝",
    "🙏",
    "✍",
    "💅",
    "🤳",
    "💪",
    "🦵",
    "🦶",
    "👂",
    "👃",
    "🧠",
    "🦷",
    "🦴",
    "👀",
    "👁",
    "👅",
    "👄",
    "👶",
    "🧒",
    "👦",
    "👧",
    "🧑",
    "👱",
    "👨",
    "🧔",
    "👨‍🦰",
    "👨‍🦱",
    "👨‍🦳",
    "👨‍🦲",
    "👩",
    "👩‍🦰",
    "👩‍🦱",
    "👩‍🦳",
    "👩‍🦲",
    "👱‍♀️",
    "👱‍♂️",
    "🧓",
    "👴",
    "👵",
    "🙍",
    "🙍‍♂️",
    "🙍‍♀️",
    "🙎",
    "🙎‍♂️",
    "🙎‍♀️",
    "🙅",
    "🙅‍♂️",
    "🙅‍♀️",
    "🙆",
    "🙆‍♂️",
    "🙆‍♀️",
    "💁",
    "💁‍♂️",
    "💁‍♀️",
    "🙋",
    "🙋‍♂️",
    "🙋‍♀️",
    "🙇",
    "🙇‍♂️",
    "🙇‍♀️",
    "🤦",
    "🤦‍♂️",
    "🤦‍♀️",
    "🤷",
    "🤷‍♂️",
    "🤷‍♀️",
    "👨‍⚕️",
    "👩‍⚕️",
    "👨‍🎓",
    "👩‍🎓",
    "👨‍🏫",
    "👩‍🏫",
    "👨‍⚖️",
    "👩‍⚖️",
    "👨‍🌾",
    "👩‍🌾",
    "👨‍🍳",
    "👩‍🍳",
    "👨‍🔧",
    "👩‍🔧",
    "👨‍🏭",
    "👩‍🏭",
    "👨‍💼",
    "👩‍💼",
    "👨‍🔬",
    "👩‍🔬",
    "👨‍💻",
    "👩‍💻",
    "👨‍🎤",
    "👩‍🎤",
    "👨‍🎨",
    "👩‍🎨",
    "👨‍✈️",
    "👩‍✈️",
    "👨‍🚀",
    "👩‍🚀",
    "👨‍🚒",
    "👩‍🚒",
    "👮",
    "👮‍♂️",
    "👮‍♀️",
    "🕵",
    "🕵️‍♂️",
    "🕵️‍♀️",
    "💂",
    "💂‍♂️",
    "💂‍♀️",
    "👷",
    "👷‍♂️",
    "👷‍♀️",
    "🤴",
    "👸",
    "👳",
    "👳‍♂️",
    "👳‍♀️",
    "👲",
    "🧕",
    "🤵",
    "👰",
    "🤰",
    "🤱",
    "👼",
    "🎅",
    "🤶",
    "🦸",
    "🦸‍♂️",
    "🦸‍♀️",
    "🦹",
    "🦹‍♂️",
    "🦹‍♀️",
    "🧙",
    "🧙‍♂️",
    "🧙‍♀️",
    "🧚",
    "🧚‍♂️",
    "🧚‍♀️",
    "🧛",
    "🧛‍♂️",
    "🧛‍♀️",
    "🧜",
    "🧜‍♂️",
    "🧜‍♀️",
    "🧝",
    "🧝‍♂️",
    "🧝‍♀️",
    "🧞",
    "🧞‍♂️",
    "🧞‍♀️",
    "🧟",
    "🧟‍♂️",
    "🧟‍♀️",
    "💆",
    "💆‍♂️",
    "💆‍♀️",
    "💇",
    "💇‍♂️",
    "💇‍♀️",
    "🚶",
    "🚶‍♂️",
    "🚶‍♀️",
    "🏃",
    "🏃‍♂️",
    "🏃‍♀️",
    "💃",
    "🕺",
    "🕴",
    "👯",
    "👯‍♂️",
    "👯‍♀️",
    "🧖",
    "🧖‍♂️",
    "🧖‍♀️",
    "🧘",
    "👭",
    "👫",
    "👬",
    "💏",
    "👨‍❤️‍💋‍👨",
    "👩‍❤️‍💋‍👩",
    "💑",
    "👨‍❤️‍👨",
    "👩‍❤️‍👩",
    "👪",
    "👨‍👩‍👦",
    "👨‍👩‍👧",
    "👨‍👩‍👧‍👦",
    "👨‍👩‍👦‍👦",
    "👨‍👩‍👧‍👧",
    "👨‍👨‍👦",
    "👨‍👨‍👧",
    "👨‍👨‍👧‍👦",
    "👨‍👨‍👦‍👦",
    "👨‍👨‍👧‍👧",
    "👩‍👩‍👦",
    "👩‍👩‍👧",
    "👩‍👩‍👧‍👦",
    "👩‍👩‍👦‍👦",
    "👩‍👩‍👧‍👧",
    "👨‍👦",
    "👨‍👦‍👦",
    "👨‍👧",
    "👨‍👧‍👦",
    "👨‍👧‍👧",
    "👩‍👦",
    "👩‍👦‍👦",
    "👩‍👧",
    "👩‍👧‍👦",
    "👩‍👧‍👧",
    "🗣",
    "👤",
    "👥",
    "👣",
    "🧳",
    "🌂",
    "☂",
    "🧵",
    "🧶",
    "👓",
    "🕶",
    "🥽",
    "🥼",
    "👔",
    "👕",
    "👖",
    "🧣",
    "🧤",
    "🧥",
    "🧦",
    "👗",
    "👘",
    "👙",
    "👚",
    "👛",
    "👜",
    "👝",
    "🎒",
    "👞",
    "👟",
    "🥾",
    "🥿",
    "👠",
    "👡",
    "👢",
    "👑",
    "👒",
    "🎩",
    "🎓",
    "🧢",
    "⛑",
    "💄",
    "💍",
    "💼",
  ]);
